import React, { useState, useEffect } from 'react';
import { X, FileText, User, Calendar, Upload, DollarSign } from 'lucide-react';
import { CollaboratorInvoice } from '../../types/collaborators';
import { collaboratorsService, timesheetsService } from '../../services/supabaseService';
import { formatCurrency, currencySymbol } from '../../utils/textUtils';

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (invoice: Partial<CollaboratorInvoice>) => void;
  invoice?: CollaboratorInvoice | null;
  // Callbacks opcionales para acciones de admin. Si no vienen, se usa
  // onSave con status='validated' / 'rejected' como fallback.
  onValidate?: (invoice: CollaboratorInvoice) => Promise<void> | void;
  onReject?: (invoice: CollaboratorInvoice, reason: string) => Promise<void> | void;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ isOpen, onClose, onSave, invoice, onValidate, onReject }) => {
  const [formData, setFormData] = useState({
    collaboratorId: '',
    collaboratorName: '',
    invoiceNumber: '',
    date: '',
    amount: 0,
    currency: 'EUR',
    attachmentUrl: '',
    attachmentName: '',
    status: 'received' as CollaboratorInvoice['status'],
    timesheetIds: [] as string[]
  });

  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [timesheets, setTimesheets] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [isProgrammerView, setIsProgrammerView] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  // Flujo de rechazo integrado en el modal: en vez de window.prompt,
  // mostramos un panel con textarea + confirmar/cancelar.
  const [rejecting, setRejecting] = useState(false);
  const [rejectionInput, setRejectionInput] = useState('');
  const [submittingAction, setSubmittingAction] = useState<'validate' | 'reject' | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
      loadCurrentUserIfNeeded();
      setRejecting(false);
      setRejectionInput('');
      setSubmittingAction(null);
    }
  }, [isOpen]);

  const loadCurrentUserIfNeeded = async () => {
    // Ejecuta siempre para determinar vista (colaborador vs admin) por
    // rol del usuario logueado. Para creación además rellena
    // collaboratorId con el propio user; para edición sólo fija el
    // flag isProgrammerView y no toca formData del invoice cargado.
    try {
      const { supabase } = await import('../../services/supabaseService');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setCurrentUserId(user.id);

      const { data: userData } = await supabase
        .from('users')
        .select('id, name, role')
        .eq('id', user.id)
        .maybeSingle();

      const isCollab = !!userData && userData.role !== 'admin' && userData.role !== 'superadmin';
      setIsProgrammerView(isCollab);

      const invoiceAny = invoice as any;
      const isCreation = !invoiceAny?.id;
      if (isCollab && isCreation) {
        setFormData(prev => ({
          ...prev,
          collaboratorId: userData!.id,
          collaboratorName: userData!.name,
          status: 'received',
        }));
        console.log('✅ Vista de colaborador (creación):', userData!.name);
      } else if (isCollab) {
        console.log('✅ Vista de colaborador (edición de su propia factura)');
      } else {
        console.log('✅ Vista de administrador');
      }
    } catch (error) {
      console.error('Error cargando usuario actual:', error);
    }
  };

  useEffect(() => {
    const invoiceAny = invoice as any;
    // Sólo tratamos como "factura existente" si trae id. Los presets
    // (por ejemplo desde TimesheetsList) llegan sin id → son creación.
    if (invoice && invoiceAny?.id) {
      console.log('📝 Cargando factura existente:', invoice);
      const rawDate = invoice.date as any;
      const isoDate = rawDate instanceof Date
        ? rawDate.toISOString().split('T')[0]
        : (typeof rawDate === 'string' ? rawDate.split('T')[0] : new Date().toISOString().split('T')[0]);
      setFormData({
        collaboratorId: invoiceAny.userId || invoice.collaboratorId,
        collaboratorName: invoiceAny.userName || invoice.collaboratorName,
        invoiceNumber: invoice.invoiceNumber,
        date: isoDate,
        amount: invoice.amount,
        currency: invoice.currency,
        attachmentUrl: invoice.attachmentUrl || '',
        attachmentName: invoice.attachmentName || '',
        status: invoice.status,
        timesheetIds: invoice.timesheetIds || []
      });
      // NO tocamos isProgrammerView aquí: lo decide loadCurrentUserIfNeeded
      // en base al rol del usuario logueado. Antes hardcodearse a false
      // hacía que Pepito al editar su propia factura pareciera admin,
      // se saltara el resubmit y RLS bloqueara el UPDATE.
    } else if (invoice) {
      // Preset para nueva factura (viene desde el parte aprobado).
      // Cargamos los defaults y dejamos que loadCurrentUserIfNeeded()
      // decida si es vista de colaborador.
      const rawDate = (invoice as any).date;
      const isoDate = rawDate instanceof Date
        ? rawDate.toISOString().split('T')[0]
        : (typeof rawDate === 'string' ? rawDate.split('T')[0] : new Date().toISOString().split('T')[0]);
      setFormData({
        collaboratorId: invoiceAny.collaboratorId || invoiceAny.userId || '',
        collaboratorName: invoiceAny.collaboratorName || invoiceAny.userName || '',
        invoiceNumber: invoice.invoiceNumber || '',
        date: isoDate,
        amount: Number(invoice.amount || 0),
        currency: invoice.currency || 'EUR',
        attachmentUrl: '',
        attachmentName: '',
        status: 'received',
        timesheetIds: invoice.timesheetIds || []
      });
    } else {
      setFormData({
        collaboratorId: '',
        collaboratorName: '',
        invoiceNumber: '',
        date: new Date().toISOString().split('T')[0],
        amount: 0,
        currency: 'EUR',
        attachmentUrl: '',
        attachmentName: '',
        status: 'received',
        timesheetIds: []
      });
    }
  }, [invoice]);

  const loadDropdownData = async () => {
    try {
      setLoadingData(true);
      console.log('🔄 Cargando colaboradores y timesheets para factura...');
      
      const [collaboratorsData, timesheetsData] = await Promise.all([
        collaboratorsService.getAll(),
        timesheetsService.getAllWithDetails()
      ]);
      
      // Filter only approved timesheets
      const approvedTimesheets = timesheetsData.filter(t => t.status === 'approved');
      
      console.log('✅ Plantilla cargados:', collaboratorsData.length);
      console.log('✅ Timesheets aprobados cargados:', approvedTimesheets.length);
      
      setCollaborators(collaboratorsData);
      setTimesheets(approvedTimesheets);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      alert('Error al cargar datos para la factura');
    } finally {
      setLoadingData(false);
    }
  };

  const handleCollaboratorChange = (collaboratorId: string) => {
    const selectedCollaborator = collaborators.find(c => c.id === collaboratorId);
    if (selectedCollaborator) {
      setFormData({
        ...formData,
        collaboratorId,
        collaboratorName: selectedCollaborator.name,
        timesheetIds: [] // Reset timesheets when changing collaborator
      });
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Tamaño máximo: 10MB');
        return;
      }

      // In a real app, you would upload to a file storage service
      const mockUrl = `/invoices/${file.name}`;
      setFormData({
        ...formData,
        attachmentUrl: mockUrl,
        attachmentName: file.name
      });
      console.log('📎 Archivo adjuntado:', file.name);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Tipo de archivo no válido. Por favor, sube un PDF o imagen (JPG/PNG)');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Tamaño máximo: 10MB');
        return;
      }

      // In a real app, you would upload to a file storage service
      const mockUrl = `/invoices/${file.name}`;
      setFormData({
        ...formData,
        attachmentUrl: mockUrl,
        attachmentName: file.name
      });
      console.log('📎 Archivo arrastrado:', file.name);
    }
  };

  const handleTimesheetToggle = (timesheetId: string) => {
    const newTimesheetIds = formData.timesheetIds.includes(timesheetId)
      ? formData.timesheetIds.filter(id => id !== timesheetId)
      : [...formData.timesheetIds, timesheetId];
    
    // Calculate total amount from selected timesheets
    const selectedTimesheets = timesheets.filter(t => newTimesheetIds.includes(t.id));
    const totalAmount = selectedTimesheets.reduce((sum, t) => sum + t.calculatedAmount, 0);
    
    setFormData({
      ...formData,
      timesheetIds: newTimesheetIds,
      amount: totalAmount
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.collaboratorId || !formData.invoiceNumber || !formData.date || !formData.amount) {
      alert('Por favor, completa todos los campos obligatorios');
      return;
    }

    // Resubmit: si un colaborador edita una factura rechazada, al
    // guardar se limpia el motivo y vuelve a 'received' para que el
    // admin la revise de nuevo.
    const invoiceAny = invoice as any;
    const isResubmit = isProgrammerView
      && invoiceAny?.id
      && (invoiceAny.status === 'rejected' || formData.status === 'rejected');

    const invoiceData: any = {
      ...formData,
      date: new Date(formData.date),
      amount: Number(formData.amount),
      timesheetIds: formData.timesheetIds || []
    };

    if (isResubmit) {
      invoiceData.status = 'received';
      invoiceData.rejectionReason = null;
      invoiceData._resubmit = true; // flag para InvoicesList → notif admin
    }

    console.log('💾 Guardando factura:', invoiceData);
    onSave(invoiceData);
  };

  const availableTimesheets = timesheets.filter(t => 
    t.collaboratorName === formData.collaboratorName
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {(invoice as any)?.id
              ? (isProgrammerView ? 'Detalle de mi Factura' : 'Revisar Factura')
              : 'Nueva Factura'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Información de la Factura</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isProgrammerView && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Colaborador {!invoice && '*'}
                  </label>
                  {invoice && formData.collaboratorName ? (
                    <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
                      {formData.collaboratorName}
                    </div>
                  ) : (
                    <select
                      required={!invoice}
                      value={formData.collaboratorId}
                      onChange={(e) => handleCollaboratorChange(e.target.value)}
                      disabled={loadingData}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">{loadingData ? 'Cargando colaboradores...' : 'Seleccionar colaborador'}</option>
                      {collaborators.map(collaborator => (
                        <option key={collaborator.id} value={collaborator.id}>
                          {collaborator.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de factura *
                </label>
                <input
                  type="text"
                  required
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ej: INV-2024-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de factura *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Colaborador viendo su propia factura ya existente:
                  mostramos el estado como badge de sólo lectura. */}
              {isProgrammerView && (invoice as any)?.id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm">
                    {formData.status === 'received' && (
                      <span className="text-blue-700 font-medium">Enviada al administrador</span>
                    )}
                    {formData.status === 'validated' && (
                      <span className="text-yellow-700 font-medium">Validada · pendiente de pago</span>
                    )}
                    {formData.status === 'paid' && (
                      <span className="text-green-700 font-medium">Pagada</span>
                    )}
                    {formData.status === 'rejected' && (
                      <span className="text-red-700 font-medium">Rechazada</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Motivo del rechazo visible para todos si aplica */}
            {formData.status === 'rejected' && (invoice as any)?.rejectionReason && (
              <div className="mt-4 p-3 border border-red-200 bg-red-50 rounded-lg">
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                  Motivo del rechazo
                </p>
                <p className="text-sm text-red-900 whitespace-pre-wrap">
                  {(invoice as any).rejectionReason}
                </p>
              </div>
            )}
          </div>

          {/* Amount and Currency */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <DollarSign className="w-5 h-5" />
              <span>Importe</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Importe *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500 text-sm">
                    {currencySymbol(formData.currency)}
                  </span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1800.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Moneda *
                </label>
                <select
                  required
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <Upload className="w-5 h-5" />
              <span>Archivo de Factura</span>
            </h3>
            
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {formData.attachmentName ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">{formData.attachmentName}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, attachmentUrl: '', attachmentName: '' })}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Arrastra el archivo aquí o haz clic para seleccionar</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="invoice-file"
                  />
                  <label
                    htmlFor="invoice-file"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                  >
                    Seleccionar archivo
                  </label>
                  <p className="text-xs text-gray-500 mt-2">PDF, JPG, PNG hasta 10MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Associated Timesheets */}
          {formData.collaboratorName && availableTimesheets.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Partes de Horas Asociados
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona los partes de horas que incluye esta factura:
                </p>
                
                <div className="space-y-2">
                  {availableTimesheets.map(timesheet => {
                    const timesheetDate = timesheet.date instanceof Date ? timesheet.date.toLocaleDateString() : timesheet.date;
                    return (
                    <div key={timesheet.id} className="flex items-center justify-between p-3 bg-white rounded border">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`timesheet-${timesheet.id}`}
                          checked={formData.timesheetIds.includes(timesheet.id)}
                          onChange={() => handleTimesheetToggle(timesheet.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`timesheet-${timesheet.id}`} className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {timesheet.projectName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {timesheetDate} • {timesheet.hours}h • {timesheet.calculatedAmount}€
                          </div>
                        </label>
                      </div>
                    </div>
                  )})}
                </div>
                
                {formData.timesheetIds.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                    <p className="text-sm text-blue-700">
                      <strong>Total calculado:</strong> {formatCurrency(formData.amount, formData.currency)}
                      ({formData.timesheetIds.length} partes seleccionados)
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <div className="flex items-center gap-2">
              {/* Botones de admin para revisar: validar / rechazar. Sólo
                  aparecen si la factura existe, no somos el propio colab,
                  y el estado permite acción (recibida). En modo rechazo
                  los ocultamos porque el panel controla el flujo. */}
              {(invoice as any)?.id && !isProgrammerView && formData.status === 'received' && !rejecting && (
                <>
                  <button
                    type="button"
                    disabled={submittingAction !== null}
                    onClick={async () => {
                      try {
                        setSubmittingAction('validate');
                        if (onValidate) {
                          await onValidate(invoice as CollaboratorInvoice);
                        } else {
                          await onSave({ ...formData, status: 'validated' } as any);
                        }
                        onClose();
                      } catch (err: any) {
                        console.error('Error validando factura:', err);
                        alert('Error validando factura: ' + (err?.message || err));
                      } finally {
                        setSubmittingAction(null);
                      }
                    }}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold disabled:opacity-50"
                  >
                    {submittingAction === 'validate' ? 'Validando…' : '✓ Validar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRejecting(true);
                      setRejectionInput('');
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    ✕ Rechazar
                  </button>
                </>
              )}
              {!rejecting && (
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {(invoice as any)?.id ? 'Guardar cambios' : 'Enviar factura'}
                </button>
              )}
            </div>
          </div>

          {/* Panel de rechazo integrado en el modal */}
          {rejecting && (
            <div className="mt-4 p-4 border border-red-200 bg-red-50 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-semibold text-red-800 mb-1">
                  Motivo del rechazo
                </label>
                <p className="text-xs text-red-700 mb-2">
                  Se enviará al colaborador para que corrija la factura.
                </p>
                <textarea
                  value={rejectionInput}
                  onChange={(e) => setRejectionInput(e.target.value)}
                  rows={4}
                  autoFocus
                  className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
                  placeholder="Ej: importe incorrecto, falta el IVA, número de factura repetido…"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRejecting(false);
                    setRejectionInput('');
                  }}
                  disabled={submittingAction !== null}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={!rejectionInput.trim() || submittingAction !== null}
                  onClick={async () => {
                    const reason = rejectionInput.trim();
                    if (!reason) return;
                    const target = invoice as CollaboratorInvoice | null | undefined;
                    if (!target || !(target as any).id) {
                      console.error('Rechazar sin factura cargada', { invoice });
                      alert('No se pudo identificar la factura a rechazar. Cierra el modal y vuelve a abrirlo.');
                      return;
                    }
                    try {
                      setSubmittingAction('reject');
                      if (onReject) {
                        await onReject(target, reason);
                      } else {
                        await onSave({ ...formData, status: 'rejected', rejectionReason: reason } as any);
                      }
                      onClose();
                    } catch (err: any) {
                      console.error('Error rechazando factura:', err);
                      alert('Error rechazando factura: ' + (err?.message || err));
                    } finally {
                      setSubmittingAction(null);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold"
                >
                  {submittingAction === 'reject' ? 'Rechazando…' : 'Confirmar rechazo'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default InvoiceModal;